import { Navigate } from 'react-router'

import {
  AuthAbout,
  CreatePassword,
  createPassword,
  createPasswordAfterRecover,
  Login,
  RecoverPassword,
  Registration,
  SharedRequest,
  validateHashOnSettingPasswordAfterRecover,
  validateHashOnSettingPasswordAfterRegistration,
} from '../components/Auth'
import { EmailRawLayout } from '../components/Email'
import { ErrorPage } from '../components/ErrorPage'
import { FilterState } from '../components/Filter'
import { Library } from '../components/Library'
import { Loader } from '../components/Loader'
import {
  RouteRedirectExternal,
  RouteRemoveRedesignPrefix,
} from '../components/Route'
import { APP_STATUS_ERROR } from '../constants/common'
import {
  PROFILE_ROUTE_NAMES,
  ROUTE_PATH_LIST,
  ROUTE_PATH_LIST_AUTH,
  ROUTE_UI_SECTION_NAME,
  ROUTER_EXTERNAL_REDIRECT_PREFIX,
  SEARCH_USER_PARAM_EMAIL,
  SEARCH_USER_PARAM_PHONE,
} from '../constants/route'
import { getMenuItems, getSettingsMenuItems } from '../utils/menu'
import {
  createSearchProfileLoader,
  createServiceRequestProfileLoader,
  getAvailableSectionWithRedirectToWebappLoader,
  normalizePhoneForSearchProfileRoute,
} from '../utils/route'

import { getAppLayoutRoute } from './appLayoutRoute'
import { getConnectedRequestThreadRawRoutes } from './connectedRequestThreadRawRoutes'
import { getEmailRawRoutes } from './emailRawRoutes'
import { getLayoutRoutes } from './layoutRoutes'
import { libraryRoutes } from './libraryRoutes'
import { getRequestThreadRawRoutes } from './requestThreadRawRoutes'
import { getSettingsRoutes } from './settingsRoutes'
import { getWorkerAppRoutes } from './workerAppRoutes'

export function getAppRoutes({
  appAvailable,
  isWorker,
  sections,
  ...restProps
}) {
  if (isWorker) {
    return getWorkerAppRoutes({ appAvailable })
  }

  return [
    getAppLayoutRoute({
      children: getLayoutRoutes({ ...restProps, appAvailable, sections }),
      getMenuItems,
      path: '/',
    }),
    getAppLayoutRoute({
      children: getSettingsRoutes({
        availableSections: restProps.availableSections,
        sections,
      }),
      getMenuItems: getSettingsMenuItems,
      path: ROUTE_PATH_LIST.settings,
    }),
    {
      HydrateFallback: Loader,
      children: getEmailRawRoutes(),
      element: <EmailRawLayout />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST.mails,
    },
    {
      HydrateFallback: Loader,
      children: getRequestThreadRawRoutes(),
      element: <EmailRawLayout />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST.serviceRequestsThreads,
    },
    {
      HydrateFallback: Loader,
      children: getConnectedRequestThreadRawRoutes(),
      element: <EmailRawLayout />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST.connectedRequestThreads,
    },
    {
      HydrateFallback: Loader,
      element: <Login />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      loader: loaderProps =>
        getAvailableSectionWithRedirectToWebappLoader({
          ...loaderProps,
          hasAccess: appAvailable,
          redirectSectionName: 'login',
        }),
      path: ROUTE_PATH_LIST_AUTH.login(),
    },
    {
      element: (
        <CreatePassword
          invalidHashProps={{
            restartLink: ROUTE_PATH_LIST_AUTH.recoverPassword,
            text: 'Auth.InvalidHashAfterPasswordRecoverKey',
          }}
          text='Auth.CreatePasswordAfterRecoverTextKey'
          onSubmit={createPasswordAfterRecover}
          onValidateHash={validateHashOnSettingPasswordAfterRecover}
        />
      ),
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST_AUTH.setPasswordAfterRecover,
    },
    {
      element: (
        <CreatePassword
          invalidHashProps={{
            restartLink: ROUTE_PATH_LIST_AUTH.registration,
            text: 'Auth.InvalidHashAfterRegistrationRecoverKey',
          }}
          onSubmit={createPassword}
          onValidateHash={validateHashOnSettingPasswordAfterRegistration}
        />
      ),
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST_AUTH.setPasswordAfterRegistration,
    },
    {
      element: <SharedRequest />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      loader: ({ params }) => {
        if (
          !restProps.availableSections?.[ROUTE_UI_SECTION_NAME.serviceRequest]
        ) {
          throw new Response('Redirect to webapp', {
            headers: {
              Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}${PROFILE_ROUTE_NAMES.sharedRequest(params.hash)}`,
            },
            status: 302,
            statusText: 'Redirect to webapp',
          })
        }

        return null
      },
      path: ROUTE_PATH_LIST_AUTH.sharedRequest,
    },
    {
      element: <Loader />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      loader: createServiceRequestProfileLoader(restProps),
      path: ROUTE_PATH_LIST.serviceRequest,
    },
    {
      element: <RecoverPassword />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST_AUTH.recoverPassword,
    },
    {
      element: <Registration />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST_AUTH.registration,
    },
    {
      element: <AuthAbout />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: ROUTE_PATH_LIST_AUTH.about,
    },
    {
      element: <FilterState />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: '/filter-state/:filterStateKey',
    },
    {
      loader: createSearchProfileLoader({
        ...restProps,
        param: SEARCH_USER_PARAM_EMAIL,
      }),
      path: `/search/profile/email/:${SEARCH_USER_PARAM_EMAIL}/`,
    },
    {
      loader: createSearchProfileLoader({
        ...restProps,
        normalize: normalizePhoneForSearchProfileRoute,
        param: SEARCH_USER_PARAM_PHONE,
      }),
      path: `/search/profile/phone/:${SEARCH_USER_PARAM_PHONE}/`,
    },
    {
      children: libraryRoutes,
      element: <Library />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      path: '/library/*',
    },

    {
      element: <RouteRedirectExternal />,
      path: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}/*`,
    },
    { element: <RouteRemoveRedesignPrefix />, path: '/redesign/*' },
    { element: <Navigate replace to='/' />, path: '/redesign' },
  ]
}
